import {
    Nature3DDemo,
    HabitatDemo3D,
    PlantLifeCycle3D,
    CellDemo3D,
    OpticsDemo3D,
  } from "./ScienceTools/ElementaryScienceTool";
import FiveSensesSVG from "./ScienceTools/FiveSensesSVG";

export const ScienceLessonPages = {
    1: {
        // Chapter 1: What is Science?
        1: [
          {
            title: "What is Science?",
            content: (
              <>
                <p>Science is about exploring and understanding the world!</p>
                <p>
                  <strong>Scientists ask questions and look for answers.</strong>
                </p>
                <ul>
                  <li>They observe things carefully—like how bugs behave!</li>
                  <li>They do experiments to learn new facts.</li>
                  <li>They measure, record data, and share results.</li>
                  <li>Science helps us discover amazing things about our planet!</li>
                </ul>
              </>
            ),
          },
          {
            title: "What do Scientists Study?",
            content: (
              <>
                <ul>
                  <li>Animals (like birds and insects)</li>
                  <li>Plants and trees</li>
                  <li>The sky and space</li>
                  <li>Water, rocks, and the earth</li>
                  <li>How things work!</li>
                </ul>
                <p>
                  <strong>Example:</strong> Why do leaves fall in autumn? Why does ice melt?
                </p>
              </>
            ),
          },
          {
            title: "How Do Scientists Work?",
            content: (
              <>
                <ul>
                  <li>
                    <strong>Observe</strong>: Watch and notice details.
                  </li>
                  <li>
                    <strong>Ask</strong>: Wonder about the world—ask questions!
                  </li>
                  <li>
                    <strong>Test</strong>: Do experiments to find answers.
                  </li>
                  <li>
                    <strong>Share</strong>: Tell others what they learned.
                  </li>
                </ul>
                <p>
                  <em>Science is like being a detective!</em>
                </p>
              </>
            ),
          },
          {
            title: "Science in Everyday Life",
            content: (
              <>
                <p>
                  <strong>Science helps us:</strong>
                </p>
                <ul>
                  <li>Grow healthy food</li>
                  <li>Build homes and schools</li>
                  <li>Make medicine</li>
                  <li>Understand the weather</li>
                  <li>Protect animals and nature</li>
                </ul>
              </>
            ),
          },
          {
            title: "Fun: 3D Nature Demo",
            content: (
              <>
                <p>
                  Scientists study things in nature like <strong>flowers</strong>, <strong>trees</strong>, and <strong>bees</strong>!
                </p>
                <Nature3DDemo />
                <p>Click "Next" to learn about Living and Non-Living things!</p>
              </>
            ),
          },
        ],
    },
    2: {
      // Chapter 1: Our Five Senses
      1: [
        {
          title: "Introduction: Our Amazing Senses",
          content: (
            <>
              <p>
                <strong>Did you know you use your senses every day to learn about the world?</strong>
              </p>
              <ul>
                <li>We have five main senses: sight, hearing, smell, taste, and touch.</li>
                <li>Our senses help us stay safe and enjoy life!</li>
              </ul>
            </>
          ),
        },
        {
          title: "Sight: Seeing the World",
          content: (
            <>
              <p>Our eyes help us see colors, shapes, and movement.</p>
              <p>
                <strong>Try this:</strong> Look around your room. What colors do you notice?
              </p>
              <p>
                <em>Fun fact: Some animals can see in the dark or see more colors than humans!</em>
              </p>
            </>
          ),
        },
        {
          title: "Hearing and Sound",
          content: (
            <>
              <p>
                Our ears help us listen to music, voices, and sounds around us.
              </p>
              <ul>
                <li>Some animals hear better than people. Bats use <strong>echolocation</strong> to find their way in the dark!</li>
              </ul>
              <p>
                <strong>Experiment:</strong> Close your eyes and listen—how many different sounds can you count?
              </p>
            </>
          ),
        },
        {
          title: "Smell, Taste, and Touch",
          content: (
            <>
              <ul>
                <li><strong>Smell:</strong> Our noses help us enjoy flowers and warn us of danger (like smoke).</li>
                <li><strong>Taste:</strong> Our tongues tell us if food is sweet, sour, salty, or bitter.</li>
                <li><strong>Touch:</strong> Our skin helps us feel hot, cold, soft, or sharp things.</li>
              </ul>
              <p>
                <strong>Can you name your favorite smell? Taste?</strong>
              </p>
            </>
          ),
        },
        {
          title: "How Senses Help Us",
          content: (
            <>
              <p>
                Our senses help us:
              </p>
              <ul>
                <li>Find yummy food</li>
                <li>Avoid things that might hurt</li>
                <li>Communicate with others</li>
              </ul>
              <p>
                <em>In the future, scientists are inventing new tools to help people who lose a sense (like special glasses for the blind)!</em>
              </p>
            </>
          ),
        },
        {
            title: "Fun: Explore Your Senses",
            content: (
              <>
                <FiveSensesSVG />
                <p>Try clicking through all five senses in 3D!</p>
              </>
            ),
          },
      ],
      // Chapter 2: Habitats and Homes
      2: [
        {
          title: "What is a Habitat?",
          content: (
            <>
              <p>
                A <strong>habitat</strong> is a place where animals and plants live.
              </p>
              <ul>
                <li>Forests, ponds, deserts, and oceans are all habitats.</li>
                <li>
                  Every living thing needs a safe home with food, water, and shelter.
                </li>
              </ul>
            </>
          ),
        },
        {
          title: "Animals and Their Homes",
          content: (
            <>
              <p>
                Different animals live in different habitats.
              </p>
              <ul>
                <li>Bears live in forests.</li>
                <li>Frogs live near ponds.</li>
                <li>Camels live in deserts.</li>
              </ul>
              <p>
                <strong>Challenge:</strong> Where would you find a penguin? (Hint: Cold places!)
              </p>
            </>
          ),
        },
        {
          title: "Plant Habitats",
          content: (
            <>
              <p>
                Plants also have special homes!
              </p>
              <ul>
                <li>Cacti grow in dry deserts.</li>
                <li>Lily pads float in ponds.</li>
                <li>Pine trees grow in cold places.</li>
              </ul>
            </>
          ),
        },
        {
          title: "Changing Habitats",
          content: (
            <>
              <p>
                Habitats can change because of weather, people, or other animals.
              </p>
              <ul>
                <li>Forests can be cut down.</li>
                <li>Floods can change rivers.</li>
              </ul>
              <p>
                <em>Scientists work to protect habitats for the future!</em>
              </p>
            </>
          ),
        },
        {
          title: "Review: Why Habitats Matter",
          content: (
            <>
              <p>
                Every living thing needs the right habitat to survive.
              </p>
              <ul>
                <li>Protecting habitats helps animals, plants, and people!</li>
              </ul>
            </>
          ),
        },
        {
            title: "Fun: Explore Habitats",
            content: (
              <>
                <HabitatDemo3D />
                <p>Click through different habitats to see what lives there!</p>
              </>
            ),
          },
      ],
    },
  
    3: {
      // Chapter 1: Plant Life Cycles
      1: [
        {
          title: "Introduction: What is a Life Cycle?",
          content: (
            <>
              <p>
                A <strong>life cycle</strong> is the journey a living thing takes from birth to growth to adulthood and then starting again.
              </p>
              <p>
                Plants have fascinating life cycles!
              </p>
            </>
          ),
        },
        {
          title: "How Plants Grow",
          content: (
            <>
              <ul>
                <li>All plants start as seeds.</li>
                <li>With water, soil, and sunlight, seeds sprout roots and shoots.</li>
                <li>Plants grow leaves, flowers, and sometimes fruits.</li>
              </ul>
              <p>
                <strong>Question:</strong> Have you ever planted a seed? What happened?
              </p>
            </>
          ),
        },
        {
          title: "Pollination and Seeds",
          content: (
            <>
              <p>
                Flowers need help to make seeds. Bees, wind, or even rain can move pollen from flower to flower.
              </p>
              <ul>
                <li>This is called <strong>pollination</strong>.</li>
                <li>After pollination, new seeds grow and the cycle starts again!</li>
              </ul>
            </>
          ),
        },
        {
          title: "Why Plant Life Cycles Matter",
          content: (
            <>
              <p>
                Plants give us oxygen, food, and beautiful flowers.
              </p>
              <ul>
                <li>Understanding plant cycles helps farmers grow crops and scientists protect nature.</li>
              </ul>
              <p>
                <em>New technology helps us study plants from space using satellites!</em>
              </p>
            </>
          ),
        },
        {
          title: "Fun: Grow Your Own Plant",
          content: (
            <>
              <p>
                Try growing a bean in a cup at home! Watch how fast it sprouts and changes.
              </p>
              <p>
                <strong>Share your plant photos with the class!</strong>
              </p>
            </>
          ),
        },
        {
            title: "Fun: Plant Life Cycle Visual",
            content: (
              <>
                <PlantLifeCycle3D />
                <p>See how a plant grows, flowers, and produces seeds.</p>
              </>
            ),
          },          
      ],
      // Chapter 2: Forces and Movement
      2: [
        {
          title: "What are Forces?",
          content: (
            <>
              <p>
                <strong>Forces</strong> are pushes or pulls that can make things move or stop.
              </p>
              <ul>
                <li>We use forces every time we kick a ball or open a door.</li>
              </ul>
            </>
          ),
        },
        {
          title: "Types of Forces",
          content: (
            <>
              <ul>
                <li><strong>Push:</strong> Moving something away from you.</li>
                <li><strong>Pull:</strong> Bringing something closer.</li>
                <li><strong>Gravity:</strong> The force that pulls things down to Earth.</li>
              </ul>
            </>
          ),
        },
        {
          title: "Friction and Movement",
          content: (
            <>
              <p>
                <strong>Friction</strong> is a force that slows things down when they rub together.
              </p>
              <p>
                <em>Try sliding on a smooth floor vs. a carpet—where is it easier?</em>
              </p>
            </>
          ),
        },
        {
          title: "Forces in Everyday Life",
          content: (
            <>
              <p>
                Forces help us ride bikes, play sports, and even build roller coasters!
              </p>
              <p>
                <strong>Future idea:</strong> Engineers use their understanding of forces to build safer cars and faster trains!
              </p>
            </>
          ),
        },
        {
          title: "Review: Why Study Forces?",
          content: (
            <>
              <p>
                Learning about forces helps us understand how things move and why.
              </p>
              <ul>
                <li>
                  <strong>Question:</strong> Can you find three examples of forces at home?
                </li>
              </ul>
            </>
          ),
        },       
      ],
    },
  
    4: {
      // Chapter 1: The Water Cycle
      1: [
        {
          title: "Introduction: The Water Cycle",
          content: (
            <>
              <p>
                Water is always moving in a big, never-ending loop called the <strong>water cycle</strong>.
              </p>
              <p>
                <strong>Why does it rain? Where does water go after it rains?</strong>
              </p>
            </>
          ),
        },
        {
          title: "Evaporation: Water Goes Up",
          content: (
            <>
              <p>
                When the sun shines, water in rivers, lakes, and oceans heats up and turns into a gas called <strong>water vapor</strong>.
              </p>
              <p>
                This is called <strong>evaporation</strong>.
              </p>
            </>
          ),
        },
        {
          title: "Condensation: Clouds Form",
          content: (
            <>
              <p>
                Water vapor cools down high in the sky and turns into tiny drops, making clouds. This is <strong>condensation</strong>.
              </p>
            </>
          ),
        },
        {
          title: "Precipitation: Rain, Snow, and Hail",
          content: (
            <>
              <p>
                When clouds get heavy, water falls back to earth as <strong>rain</strong>, <strong>snow</strong>, or <strong>hail</strong>. This is called <strong>precipitation</strong>.
              </p>
            </>
          ),
        },
        {
          title: "Collection and Repeat!",
          content: (
            <>
              <p>
                Water collects in rivers, lakes, and oceans, and the cycle starts again.
              </p>
              <ul>
                <li>
                  <strong>Challenge:</strong> Can you draw your own water cycle?
                </li>
              </ul>
              <p>
                <em>Scientists use satellites to study the water cycle and predict the weather!</em>
              </p>
            </>
          ),
        },        
      ],
      // Chapter 2: Energy All Around Us
      2: [
        {
          title: "What is Energy?",
          content: (
            <>
              <p>
                <strong>Energy</strong> makes things happen. It powers our lights, cars, and even our bodies!
              </p>
              <ul>
                <li>We get energy from food, the sun, and electricity.</li>
              </ul>
            </>
          ),
        },
        {
          title: "Types of Energy",
          content: (
            <>
              <ul>
                <li><strong>Light energy:</strong> From the sun or light bulbs</li>
                <li><strong>Heat energy:</strong> From fire, the sun, or heaters</li>
                <li><strong>Sound energy:</strong> From music and talking</li>
                <li><strong>Movement (kinetic) energy:</strong> From moving things</li>
              </ul>
            </>
          ),
        },
        {
          title: "Energy in Nature",
          content: (
            <>
              <p>
                Plants use the sun’s energy to grow—this is called <strong>photosynthesis</strong>.
              </p>
              <p>
                <em>Renewable energy like wind and solar power can help protect our planet.</em>
              </p>
            </>
          ),
        },
        {
          title: "Saving Energy",
          content: (
            <>
              <p>
                Turning off lights and recycling help save energy for the future.
              </p>
              <p>
                <strong>Challenge:</strong> How can you save energy at home or school?
              </p>
            </>
          ),
        },
        {
          title: "Review: Why is Energy Important?",
          content: (
            <>
              <ul>
                <li>Energy helps us live, play, and learn.</li>
                <li>
                  Scientists are inventing new ways to create clean energy for the future!
                </li>
              </ul>
            </>
          ),
        },       
      ],
    },
  
    5: {
      // Chapter 1: Human Body Systems
      1: [
        {
          title: "Introduction: The Human Body",
          content: (
            <>
              <p>
                Our body is made up of amazing systems that work together!
              </p>
              <ul>
                <li>Can you name parts of your body?</li>
                <li>Let’s explore how they work together.</li>
              </ul>
            </>
          ),
        },
        {
          title: "Skeletal and Muscular Systems",
          content: (
            <>
              <ul>
                <li>
                  <strong>Skeletal system:</strong> Bones give us shape and support.
                </li>
                <li>
                  <strong>Muscular system:</strong> Muscles help us move.
                </li>
                <li>
                  <em>Question:</em> Can you feel your bones and muscles working?
                </li>
              </ul>
            </>
          ),
        },
        {
          title: "The Digestive System",
          content: (
            <>
              <p>
                When we eat, our body breaks down food to give us energy.
              </p>
              <ul>
                <li>
                  <strong>Challenge:</strong> Trace the path of food from mouth to stomach!
                </li>
              </ul>
            </>
          ),
        },
        {
          title: "The Circulatory System",
          content: (
            <>
              <ul>
                <li>
                  <strong>Heart:</strong> Pumps blood everywhere in our body.
                </li>
                <li>
                  <strong>Blood:</strong> Delivers oxygen and nutrients.
                </li>
              </ul>
              <p>
                <em>Scientists use technology to study our hearts and invent new medicines!</em>
              </p>
            </>
          ),
        },
        {
          title: "Review: Body Systems",
          content: (
            <>
              <ul>
                <li>All body systems work together so we can grow, learn, and play.</li>
                <li>
                  <strong>Can you jump, run, or eat without your bones and muscles?</strong>
                </li>
              </ul>
            </>
          ),
        },        
      ],
      // Chapter 2: Simple Machines
      2: [
        {
          title: "What is a Simple Machine?",
          content: (
            <>
              <p>
                Simple machines help us do work more easily.
              </p>
              <ul>
                <li>Examples: lever, wheel and axle, pulley, inclined plane, screw, wedge</li>
              </ul>
            </>
          ),
        },
        {
          title: "Levers and Pulleys",
          content: (
            <>
              <ul>
                <li>
                  <strong>Lever:</strong> A bar that moves around a point to lift things (like a seesaw).
                </li>
                <li>
                  <strong>Pulley:</strong> Uses wheels and ropes to lift heavy objects.
                </li>
              </ul>
              <p>
                <em>Where do you see levers and pulleys in real life?</em>
              </p>
            </>
          ),
        },
        {
          title: "Inclined Planes, Screws, and Wedges",
          content: (
            <>
              <ul>
                <li>
                  <strong>Inclined plane:</strong> A ramp that makes lifting things easier.
                </li>
                <li>
                  <strong>Screw:</strong> A ramp wrapped around a post, used to hold things together.
                </li>
                <li>
                  <strong>Wedge:</strong> Helps split things apart (like an axe).
                </li>
              </ul>
            </>
          ),
        },
        {
          title: "Simple Machines in Everyday Life",
          content: (
            <>
              <p>
                Scissors, door handles, and even bikes use simple machines!
              </p>
              <p>
                <strong>Challenge:</strong> Can you find simple machines at home?
              </p>
            </>
          ),
        },
        {
          title: "Review: Why Study Simple Machines?",
          content: (
            <>
              <ul>
                <li>Simple machines make life easier and inspire inventors and engineers!</li>
                <li>
                  <em>Can you invent a new machine to help at home or school?</em>
                </li>
              </ul>
            </>
          ),
        },     
      ],
    },
  
    6: {
      // Chapter 1: Diversity of Living Things
      1: [
        {
          title: "What is Biodiversity?",
          content: (
            <>
              <p>
                <strong>Biodiversity</strong> means the variety of living things on Earth—plants, animals, fungi, and more!
              </p>
              <p>
                Every species is important for a healthy planet.
              </p>
            </>
          ),
        },
        {
          title: "Classifying Living Things",
          content: (
            <>
              <p>
                Scientists group living things based on their features.
              </p>
              <ul>
                <li>Mammals, birds, fish, reptiles, amphibians, insects...</li>
                <li>Plants can be trees, grasses, or flowers.</li>
              </ul>
            </>
          ),
        },
        {
          title: "Adaptations for Survival",
          content: (
            <>
              <p>
                Living things adapt to their environment to survive.
              </p>
              <ul>
                <li>Penguins have blubber for cold.</li>
                <li>Cacti store water in deserts.</li>
                <li>Birds migrate to warmer places.</li>
              </ul>
            </>
          ),
        },
        {
          title: "Threats to Biodiversity",
          content: (
            <>
              <p>
                Pollution, habitat loss, and climate change put species at risk.
              </p>
              <p>
                <em>Future scientists are working on solutions to protect biodiversity!</em>
              </p>
            </>
          ),
        },
        {
          title: "Why Protect Biodiversity?",
          content: (
            <>
              <ul>
                <li>Biodiversity keeps ecosystems healthy.</li>
                <li>Humans depend on many species for food, medicine, and clean air!</li>
              </ul>
            </>
          ),
        },         
      ],
      // Chapter 2: Electricity and Circuits
      2: [
        {
          title: "What is Electricity?",
          content: (
            <>
              <p>
                <strong>Electricity</strong> is a type of energy that powers our homes, schools, and gadgets.
              </p>
              <p>
                We can’t see electricity, but we can see what it does!
              </p>
            </>
          ),
        },
        {
          title: "Building a Simple Circuit",
          content: (
            <>
              <ul>
                <li>A <strong>circuit</strong> is a path for electricity to flow.</li>
                <li>Wires connect a battery to a light bulb.</li>
                <li>If the path is closed, the light turns on!</li>
              </ul>
              <p>
                <strong>Try making a simple circuit with a battery and bulb (with adult help)!</strong>
              </p>
            </>
          ),
        },
        {
          title: "Conductors and Insulators",
          content: (
            <>
              <p>
                <strong>Conductors</strong> let electricity flow (like metal).</p>
              <p>
                <strong>Insulators</strong> block electricity (like plastic or rubber).</p>
              <p>
                <em>Why are wires covered in plastic?</em>
              </p>
            </>
          ),
        },
        {
          title: "Staying Safe with Electricity",
          content: (
            <>
              <ul>
                <li>Never touch outlets or wires without help.</li>
                <li>Water and electricity are a dangerous mix!</li>
              </ul>
              <p>
                <em>In the future, clean electricity from wind and sun can help save our planet!</em>
              </p>
            </>
          ),
        },
        {
          title: "Review: Why Study Electricity?",
          content: (
            <>
              <ul>
                <li>Understanding electricity lets us invent new technology!</li>
                <li>
                  <strong>Can you think of a gadget you want to invent?</strong>
                </li>
              </ul>
            </>
          ),
        },         
      ],
    },
  
    7: {
      // Chapter 1: Ecosystems & Interactions
      1: [
        {
          title: "What is an Ecosystem?",
          content: (
            <>
              <p>
                An <strong>ecosystem</strong> is a community of living and non-living things interacting together.
              </p>
              <p>
                Forests, ponds, and even cities are ecosystems!
              </p>
            </>
          ),
        },
        {
          title: "Producers, Consumers, Decomposers",
          content: (
            <>
              <ul>
                <li><strong>Producers:</strong> Make food (like plants).</li>
                <li><strong>Consumers:</strong> Eat plants or animals (like humans and wolves).</li>
                <li><strong>Decomposers:</strong> Break down dead things (like mushrooms and bacteria).</li>
              </ul>
              <p>
                <em>Why are decomposers important?</em>
              </p>
            </>
          ),
        },
        {
          title: "Food Chains and Webs",
          content: (
            <>
              <p>
                A <strong>food chain</strong> shows who eats whom in an ecosystem.
              </p>
              <p>
                Many chains connect to make a <strong>food web</strong>.
              </p>
              <p>
                <strong>Challenge:</strong> Can you make your own food web?
              </p>
            </>
          ),
        },
        {
          title: "Ecosystem Changes",
          content: (
            <>
              <p>
                Ecosystems can change because of weather, animals, or humans.
              </p>
              <p>
                <em>How can we keep ecosystems healthy for the future?</em>
              </p>
            </>
          ),
        },
        {
          title: "Why Study Ecosystems?",
          content: (
            <>
              <ul>
                <li>Healthy ecosystems support life on Earth!</li>
                <li>
                  <em>Scientists use computer models to predict how ecosystems will change over time.</em>
                </li>
              </ul>
            </>
          ),
        },      
      ],
      // Chapter 2: Heat and Particle Theory
      2: [
        {
          title: "What is Heat?",
          content: (
            <>
              <p>
                <strong>Heat</strong> is energy that makes things warmer.
              </p>
              <ul>
                <li>The sun warms the Earth.</li>
                <li>We use heat to cook food and stay cozy.</li>
              </ul>
            </>
          ),
        },
        {
          title: "How Does Heat Move?",
          content: (
            <>
              <ul>
                <li><strong>Conduction:</strong> Heat moves through touch (like a hot pan handle).</li>
                <li><strong>Convection:</strong> Heat moves through liquids and gases (like soup warming up).</li>
                <li><strong>Radiation:</strong> Heat moves through empty space (like sunlight).</li>
              </ul>
              <p>
                <em>Where do you feel heat every day?</em>
              </p>
            </>
          ),
        },
        {
          title: "Particle Theory Basics",
          content: (
            <>
              <p>
                Everything is made of tiny particles (atoms and molecules). They move more when things get hot!
              </p>
              <p>
                <strong>Try this:</strong> Watch how sugar dissolves faster in hot water than in cold!
              </p>
            </>
          ),
        },
        {
          title: "Why Study Heat?",
          content: (
            <>
              <ul>
                <li>Understanding heat helps us invent cool tech like fridges and stoves.</li>
                <li>
                  <em>Engineers are designing future homes that use heat more efficiently to save energy!</em>
                </li>
              </ul>
            </>
          ),
        },      
      ],
    },
  
    8: {
      // Chapter 1: Cells and Systems
      1: [
        {
          title: "What is a Cell?",
          content: (
            <>
              <p>
                All living things are made of tiny units called <strong>cells</strong>.
              </p>
              <p>
                Cells are the building blocks of life!
              </p>
            </>
          ),
        },
        {
          title: "Types of Cells",
          content: (
            <>
              <ul>
                <li>
                  <strong>Animal cells:</strong> Found in animals (including humans!)
                </li>
                <li>
                  <strong>Plant cells:</strong> Have cell walls and make their own food
                </li>
              </ul>
              <p>
                <em>Microscopes help us see cells up close.</em>
              </p>
            </>
          ),
        },
        {
          title: "Cells Work Together",
          content: (
            <>
              <p>
                Cells join together to make tissues, organs, and body systems.
              </p>
              <p>
                <strong>Example:</strong> Your skin is made of millions of skin cells!
              </p>
            </>
          ),
        },
        {
          title: "Systems of the Body",
          content: (
            <>
              <ul>
                <li><strong>Digestive system</strong> breaks down food.</li>
                <li><strong>Respiratory system</strong> helps us breathe.</li>
                <li><strong>Nervous system</strong> sends messages around the body.</li>
              </ul>
            </>
          ),
        },
        {
          title: "Review: Why Study Cells?",
          content: (
            <>
              <ul>
                <li>Cells help us understand health, growth, and new technologies (like medicine)!</li>
                <li>
                  <em>In the future, cell science could help us cure diseases and even grow new organs!</em>
                </li>
              </ul>
            </>
          ),
        },
        {
            title: "Zoom in: Cells and Systems",
            content: (
              <>
                <CellDemo3D />
                <p>Explore animal and plant cells in 3D!</p>
              </>
            ),
          },          
      ],
      // Chapter 2: Light and Optics
      2: [
        {
          title: "What is Light?",
          content: (
            <>
              <p>
                <strong>Light</strong> helps us see and is a form of energy.
              </p>
              <ul>
                <li>The sun is our biggest source of light.</li>
                <li>We also use flashlights, lamps, and screens.</li>
              </ul>
            </>
          ),
        },
        {
          title: "How Does Light Travel?",
          content: (
            <>
              <p>
                Light moves in straight lines, but can bounce (reflect) or bend (refract) when it hits things.
              </p>
              <p>
                <strong>Try this:</strong> Shine a flashlight on a mirror and watch the light bounce!
              </p>
            </>
          ),
        },
        {
          title: "Optics in Everyday Life",
          content: (
            <>
              <p>
                <strong>Optics</strong> is the study of how light works.
              </p>
              <p>
                Eyeglasses, cameras, and telescopes use optics.
              </p>
              <em>Future scientists use lasers and fiber optics for super-fast internet!</em>
            </>
          ),
        },
        {
          title: "The Spectrum of Light",
          content: (
            <>
              <p>
                Light comes in many colors (the spectrum).
              </p>
              <ul>
                <li>Rainbows show all the colors in sunlight!</li>
                <li>
                  Some light is invisible, like <strong>ultraviolet</strong> and <strong>infrared</strong>.
                </li>
              </ul>
            </>
          ),
        },
        {
          title: "Review: Why Study Light?",
          content: (
            <>
              <ul>
                <li>Understanding light helps us invent new tech—like lasers and solar panels!</li>
                <li>
                  <em>Can you imagine future inventions using light?</em>
                </li>
              </ul>
            </>
          ),
        },
        {
            title: "Light and Optics in 3D",
            content: (
              <>
                <OpticsDemo3D />
                <p>See how light bends, reflects, and forms rainbows!</p>
              </>
            ),
          },          
      ],
    },
  
    9: {
      // Chapter 1: Reproduction and Genetics
      1: [
        {
          title: "What is Reproduction?",
          content: (
            <>
              <p>
                Reproduction is how living things make more of themselves.
              </p>
              <ul>
                <li>
                  <strong>Plants:</strong> Use seeds, spores, or cuttings.
                </li>
                <li>
                  <strong>Animals:</strong> Have babies or lay eggs.
                </li>
              </ul>
            </>
          ),
        },
        {
          title: "Genetics: Passing Traits",
          content: (
            <>
              <p>
                <strong>Genetics</strong> is the study of how traits (like eye color) are passed from parents to offspring.
              </p>
              <p>
                DNA is like a code that tells our bodies how to grow.
              </p>
            </>
          ),
        },
        {
          title: "Variation and Inheritance",
          content: (
            <>
              <p>
                All living things have differences. These variations help species survive changes.
              </p>
              <p>
                <strong>Example:</strong> Some plants can survive with less water.
              </p>
            </>
          ),
        },
        {
          title: "Modern Genetics",
          content: (
            <>
              <p>
                Today, scientists can read and edit DNA! This helps fight disease and grow better crops.
              </p>
              <p>
                <em>But scientists must use this power carefully and ethically.</em>
              </p>
            </>
          ),
        },
        {
          title: "Review: Why Study Genetics?",
          content: (
            <>
              <ul>
                <li>Genetics helps us understand health, medicine, and even the future of food.</li>
                <li>
                  <strong>Would you want to know your DNA code?</strong>
                </li>
              </ul>
            </>
          ),
        },
      ],
      // Chapter 2: Chemistry of Everyday Life
      2: [
        {
          title: "What is Chemistry?",
          content: (
            <>
              <p>
                <strong>Chemistry</strong> is the science of what things are made of and how they change.
              </p>
              <p>
                Everything is made of tiny building blocks called <strong>atoms</strong>!
              </p>
            </>
          ),
        },
        {
          title: "States of Matter",
          content: (
            <>
              <ul>
                <li><strong>Solid:</strong> Keeps its shape (like ice).</li>
                <li><strong>Liquid:</strong> Flows and takes the shape of its container (like water).</li>
                <li><strong>Gas:</strong> Spreads everywhere (like steam).</li>
              </ul>
              <p>
                <em>Try this: Watch ice melt into water, then boil into steam!</em>
              </p>
            </>
          ),
        },
        {
          title: "Chemical Changes",
          content: (
            <>
              <p>
                When two substances mix and form something new, that’s a chemical change!
              </p>
              <ul>
                <li>
                  <strong>Example:</strong> Baking a cake or rusting iron.
                </li>
              </ul>
            </>
          ),
        },
        {
          title: "Chemistry in Daily Life",
          content: (
            <>
              <ul>
                <li>Cooking, cleaning, making medicines, and even growing plants—all use chemistry!</li>
                <li>
                  <em>Future scientists create new materials, medicines, and green energy with chemistry.</em>
                </li>
              </ul>
            </>
          ),
        },
        {
          title: "Review: Why Study Chemistry?",
          content: (
            <>
              <ul>
                <li>Chemistry explains how our world works and how we can invent things for the future!</li>
                <li>
                  <strong>What chemical change can you see at home today?</strong>
                </li>
              </ul>
            </>
          ),
        },       
      ],
    },
  
    10: {
      // Chapter 1: Sustainability and Climate Change
      1: [
        {
          title: "What is Sustainability?",
          content: (
            <>
              <p>
                <strong>Sustainability</strong> means meeting our needs without harming the planet for future generations.
              </p>
              <p>
                We need to balance people, planet, and prosperity.
              </p>
            </>
          ),
        },
        {
          title: "Climate Change: The Science",
          content: (
            <>
              <p>
                Our planet is warming up because of greenhouse gases from burning fossil fuels.
              </p>
              <ul>
                <li>
                  <strong>Evidence:</strong> Melting ice, rising sea levels, extreme weather.
                </li>
              </ul>
            </>
          ),
        },
        {
          title: "Impacts of Climate Change",
          content: (
            <>
              <ul>
                <li>More floods, droughts, and wildfires</li>
                <li>Threats to plants, animals, and people</li>
              </ul>
              <p>
                <em>Future scientists use data and computer models to understand and predict climate change.</em>
              </p>
            </>
          ),
        },
        {
          title: "Taking Action: Solutions",
          content: (
            <>
              <ul>
                <li>Switch to clean energy (solar, wind, hydro)</li>
                <li>Recycle, reduce, reuse</li>
                <li>Protect forests and oceans</li>
                <li>
                  Support new technologies (like electric cars and smart cities)
                </li>
              </ul>
            </>
          ),
        },
        {
          title: "Review: Our Role in a Sustainable Future",
          content: (
            <>
              <ul>
                <li>We all can help by making smart choices!</li>
                <li>
                  <strong>Imagine and design your own sustainable invention.</strong>
                </li>
              </ul>
            </>
          ),
        },       
      ],
      // Chapter 2: Chemical Reactions
      2: [
        {
          title: "What is a Chemical Reaction?",
          content: (
            <>
              <p>
                When substances mix and make something new, it’s called a <strong>chemical reaction</strong>.
              </p>
              <p>
                Chemical reactions happen all around us—in nature, cooking, and even inside our bodies!
              </p>
            </>
          ),
        },
        {
          title: "Types of Chemical Reactions",
          content: (
            <>
              <ul>
                <li><strong>Combustion:</strong> Burning fuel for energy</li>
                <li><strong>Photosynthesis:</strong> Plants make food using sunlight</li>
                <li><strong>Rusting:</strong> Iron reacts with oxygen</li>
              </ul>
              <p>
                <em>Chemical reactions are essential for life and technology.</em>
              </p>
            </>
          ),
        },
        {
          title: "Observing Chemical Reactions",
          content: (
            <>
              <ul>
                <li>Color change, heat, bubbles, and new smells can all show a reaction is happening.</li>
                <li>
                  <strong>Try this:</strong> Mix baking soda and vinegar—what do you notice?
                </li>
              </ul>
            </>
          ),
        },
        {
          title: "Chemical Reactions in Technology",
          content: (
            <>
              <p>
                Batteries, fuel cells, and even computers rely on chemical reactions.
              </p>
              <p>
                <em>Future chemists are inventing safer batteries and clean fuels!</em>
              </p>
            </>
          ),
        },
        {
          title: "Review: Why Study Chemical Reactions?",
          content: (
            <>
              <ul>
                <li>Understanding reactions helps us solve big challenges—like energy, health, and sustainability!</li>
                <li>
                  <strong>Can you spot a chemical reaction in your kitchen?</strong>
                </li>
              </ul>
            </>
          ),
        },         
      ],
    },
    11: {
        1: [
          {
            title: "Grade 11 - Coming Soon",
            content: (
              <p>
                Grade 11 Science content is coming soon!
              </p>
            ),
          },
        ],
        2: [
          {
            title: "Grade 11 - Coming Soon",
            content: (
              <p>
                Grade 11 Science content is coming soon!
              </p>
            ),
          },
        ],
      },
      
      12: {
        1: [
          {
            title: "Grade 12 - Coming Soon",
            content: (
              <p>
                Grade 12 Science content is coming soon!
              </p>
            ),
          },
        ],
        2: [
          {
            title: "Grade 12 - Coming Soon",
            content: (
              <p>
                Grade 12 Science content is coming soon!
              </p>
            ),
          },
        ],
      },      
  };
  